/**
 * Minimum supported app version for the iOS and Android builds.
 *
 * The web build is bundled inside the native apps, so older versions keep
 * running after a release. This lets us require an update before a change that
 * those versions can't work with. It reads public/app-config.json from the live
 * site so the minimum can change without shipping a new app.
 *
 * Fails open: a missing minimum, an unreadable config or an unparseable version
 * never blocks anyone. Nothing here runs on the web, which is always current.
 */

import { App } from '@capacitor/app';
import { getPlatform, getPublicAppUrl } from './platform';

export interface UpdateRequirement {
  /** Where to send the user, or null if no store link is configured. */
  storeUrl: string | null;
}

function parseVersion(version: string): number[] | null {
  const trimmed = version.trim();
  if (!/^\d+(\.\d+)*$/.test(trimmed)) return null;
  return trimmed.split('.').map(Number);
}

/** True only when both versions parse and current is below minimum. Missing parts count as 0. */
export function isBelowMinimum(current: string, minimum: string): boolean {
  const a = parseVersion(current);
  const b = parseVersion(minimum);
  if (!a || !b) return false;
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    const diff = (a[i] ?? 0) - (b[i] ?? 0);
    if (diff !== 0) return diff < 0;
  }
  return false;
}

/** Decide from the raw config. Anything malformed means no update is required. */
export function evaluateUpdateRequirement(
  config: unknown,
  platform: string,
  currentVersion: string,
): UpdateRequirement | null {
  if (typeof config !== 'object' || config === null) return null;
  const { minAppVersion, storeUrl } = config as {
    minAppVersion?: Record<string, unknown> | null;
    storeUrl?: Record<string, unknown> | null;
  };
  const minimum = minAppVersion?.[platform];
  if (typeof minimum !== 'string' || !isBelowMinimum(currentVersion, minimum)) return null;
  const url = storeUrl?.[platform];
  return { storeUrl: typeof url === 'string' && url ? url : null };
}

/** Check the live config against this install. Returns null if nothing is required. */
export async function checkForRequiredUpdate(): Promise<UpdateRequirement | null> {
  try {
    const [response, info] = await Promise.all([
      fetch(`${getPublicAppUrl()}/app-config.json`, { cache: 'no-store' }),
      App.getInfo(),
    ]);
    if (!response.ok) return null;
    return evaluateUpdateRequirement(await response.json(), getPlatform(), info.version);
  } catch {
    return null;
  }
}
